import React, { ReactElement, ReactFragment, ReactNode, useCallback, useContext, useState } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "affinityIcons";
import { isNil } from "ramda";
import { styled } from "styled-components";

import { Grid } from "affinityUI/elements/grid";

type ExtendedRowContextType = [Maybe<boolean>, Maybe<(state: boolean) => void>];

const Marker = styled.a`
  cursor: pointer;
  color: inherit;
`;

export const ExtendedRowContext = React.createContext<ExtendedRowContextType>([null, null]);

const ExtendedRowContextProvider: React.FC<React.PropsWithChildren<{ initiallyExtended?: boolean }>> = ({
  children,
  initiallyExtended,
}) => {
  const [extended, setExtended] = useState(Boolean(initiallyExtended));
  return <ExtendedRowContext.Provider value={[extended, setExtended]}>{children}</ExtendedRowContext.Provider>;
};

export const useExtendedRow = (): ExtendedRowContextType => useContext(ExtendedRowContext);

const Extension: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [extended] = useExtendedRow();
  return extended ? (
    <tr className="extension">
      <td colSpan={1000}>{children}</td>
    </tr>
  ) : (
    <></>
  );
};

const Control: React.FC = () => {
  const [extended, setExtended] = useExtendedRow();
  const handleOnControlClick = useCallback(() => {
    if (!isNil(setExtended)) setExtended(!extended);
  }, [extended, setExtended]);
  return isNil(extended) ? null : (
    <Marker onClick={handleOnControlClick} aria-label="Show more" role="button">
      {extended ? <ChevronUpIcon /> : <ChevronDownIcon />}
    </Marker>
  );
};

const ControlCell: React.FC<JSX.IntrinsicElements["td"]> = ({ children, ...props }) => {
  const [extended] = useExtendedRow();
  const inner = !isNil(extended) ? (
    <Grid $cols={[1, "min-content"]}>
      <span>{children}</span>
      <Control />
    </Grid>
  ) : (
    children
  );
  return <td {...props}>{inner}</td>;
};

const ExtensionMark: React.FC = () => {
  const [extended] = useExtendedRow();
  return extended ? <tr className="isExtended" /> : null;
};

export const Extendable: React.FC<React.PropsWithChildren<{ initiallyExtended?: boolean }>> & {
  Extension: typeof Extension;
  ControlCell: typeof ControlCell;
  Control: typeof Control;
} = ({ children, initiallyExtended }) => {
  return (
    <ExtendedRowContextProvider initiallyExtended={initiallyExtended}>
      <ExtensionMark />
      {children}
    </ExtendedRowContextProvider>
  );
};

Extendable.Extension = Extension;
Extendable.ControlCell = ControlCell;
Extendable.Control = Control;

const isReactFragment = (node: ReactNode): node is ReactFragment => {
  if (
    !isNil(node) &&
    typeof node !== "string" &&
    typeof node !== "number" &&
    typeof node !== "boolean" &&
    "type" in node &&
    node.type
  ) {
    return node.type === React.Fragment;
  }
  return false;
};

const replaceFirstTd = (children: ReactNode): ReactNode => {
  return React.Children.map(children, (child, index) => {
    // eslint-disable-next-line no-prototype-builtins
    if (!isNil(child) && typeof child !== "object" && !child.hasOwnProperty("props")) return child;
    const complexChild = child as ReactElement;
    if (isReactFragment(complexChild)) {
      return {
        ...complexChild,
        props: { ...complexChild.props, children: replaceFirstTd(complexChild.props.children) },
      };
    }
    const props = (child as unknown as { props: Record<string, unknown> }).props;
    if (index === 0 && !isNil(props)) return <ControlCell {...props} />;
    return child;
  });
};

export const ExtendableRow: React.FC<
  JSX.IntrinsicElements["tr"] & { initiallyExtended?: boolean; extension?: Maybe<React.ReactNode> }
> = ({ extension, initiallyExtended, children, ...props }) => {
  if (!isNil(extension)) {
    const newChildren = replaceFirstTd(children);

    return (
      <Extendable initiallyExtended={initiallyExtended}>
        <tr {...props}>{newChildren}</tr>
        <Extension>{extension}</Extension>
      </Extendable>
    );
  }
  return <tr {...props}>{children}</tr>;
};
